<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label :class="labelClasses" :style="labelStyles" v-html="label" />
        <SmartText
          :active="active"
          :editing="editing"
          :item="item"
          :style="descriptionStyles"
          :text="description"
        />
      </div>

      <div class="signature-container" :style="signatureContainerStyles">
        <div class="signature-pad__container">
          <canvas
            ref="signaturePad"
            :class="signaturePadClasses"
            :style="signaturePadStyles"
            @pointerdown="handlePointerDown"
            @pointermove="handlePointerMove"
            @pointerup="handlePointerUp"
            @pointerleave="handlePointerUp"
            @touchstart.prevent
            @click="handleClick"
          />
          <span
            v-if="!editing && item.value"
            class="signature__clear-btn"
            @click.prevent="clearPad"
          >
            <i class="fa fa-times" aria-hidden="true" />
          </span>
        </div>

        <input
          :id="item.label"
          :value="item.value"
          type="text"
          :name="item.label"
          :required="item.required"
          class="signature__pad--output"
        />
      </div>

      <ErrorMessage :item="item" />
    </div>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import signaturePlaceholder from "@/assets/images/signature_placeholder.png";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import { set } from "lodash-es";

export default {
  name: "Signature",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      signatureMode: false,
      ctx: null,
      points: [],
      lastX: 0,
      lastY: 0,
      lineWidth: 3,
      signatureColor: "#000000",
      dpr: 1,
      canvasWidth: 0,
      canvasHeight: 0,
    };
  },
  computed: {
    signaturePadClasses() {
      return {
        signature__pad: true,
        "signature__pad--disabled": this.editing || this.isDisabled,
      };
    },
    signaturePadStyles() {
      if (this.item.value) return;

      return {
        "background-image": `url(${signaturePlaceholder})`,
      };
    },
    signatureContainerStyles() {
      return {
        cursor: this.isDisabled ? "not-allowed" : "pointer",
        opacity: this.isDisabled ? 0.6 : 1,
      };
    },
  },
  mounted() {
    this.initializeCanvas();
    window.addEventListener("resize", this.handleResize);
    if (this.item.value) {
      this.loadExistingSignature();
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    initializeCanvas() {
      const canvas = this.$refs.signaturePad;
      this.ctx = canvas.getContext("2d");
      this.dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;

      // Set the canvas dimensions according to the device pixel ratio
      canvas.width = this.canvasWidth * this.dpr;
      canvas.height = this.canvasHeight * this.dpr;

      // Set the display size
      canvas.style.width = `${this.canvasWidth}px`;
      canvas.style.height = `${this.canvasHeight}px`;

      // Scale the context
      this.ctx.scale(this.dpr, this.dpr);

      this.ctx.lineCap = "round";
      this.ctx.lineJoin = "round";
      this.ctx.strokeStyle = this.signatureColor;
      this.ctx.lineWidth = this.lineWidth;
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = "high";
    },
    handleResize() {
      this.initializeCanvas();
      if (this.item.value) {
        this.loadExistingSignature();
      }
    },
    loadExistingSignature() {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

        // Calculate aspect ratio to maintain signature proportions
        const imgAspectRatio = img.width / img.height;
        const canvasAspectRatio = this.canvasWidth / this.canvasHeight;

        let drawWidth = this.canvasWidth;
        let drawHeight = this.canvasHeight;

        if (imgAspectRatio > canvasAspectRatio) {
          drawHeight = drawWidth / imgAspectRatio;
        } else {
          drawWidth = drawHeight * imgAspectRatio;
        }

        // Center the image
        const x = (this.canvasWidth - drawWidth) / 2;
        const y = (this.canvasHeight - drawHeight) / 2;

        this.ctx.drawImage(img, x, y, drawWidth, drawHeight);
      };

      img.src = this.item.value;
    },
    getPointFromEvent(event) {
      const rect = this.$refs.signaturePad.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    },
    handleClick(event) {
      if (this.editing) return;

      const point = this.getPointFromEvent(event);

      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, this.lineWidth / 2, 0, 2 * Math.PI);
      this.ctx.fillStyle = this.signatureColor;
      this.ctx.fill();

      try {
        const dataUrl = this.$refs.signaturePad.toDataURL();
        this.setCanvasURL(dataUrl);
      } catch (error) {
        console.error("Error converting signature to data URL:", error);
        this.clearPad();
      }
    },
    handlePointerDown(event) {
      if (this.editing) return;

      this.signatureMode = true;
      this.points = [];
      const point = this.getPointFromEvent(event);
      this.points.push(point);
      this.lastX = point.x;
      this.lastY = point.y;

      this.$refs.signaturePad.style.backgroundImage = "";
    },
    handlePointerMove(event) {
      if (!this.signatureMode) return;
      event.preventDefault();

      const point = this.getPointFromEvent(event);
      this.points.push(point);

      if (this.points.length > 2) {
        const xc = (point.x + this.lastX) / 2;
        const yc = (point.y + this.lastY) / 2;

        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.quadraticCurveTo(this.lastX, this.lastY, xc, yc);
        this.ctx.stroke();

        this.lastX = xc;
        this.lastY = yc;
      }
    },
    handlePointerUp() {
      if (!this.signatureMode) return;

      if (this.points.length > 1) {
        const lastPoint = this.points[this.points.length - 1];
        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.lineTo(lastPoint.x, lastPoint.y);
        this.ctx.stroke();
      }

      this.signatureMode = false;
      this.points = [];

      try {
        const dataUrl = this.$refs.signaturePad.toDataURL();
        this.setCanvasURL(dataUrl);
      } catch (error) {
        console.error("Error converting signature to data URL:", error);
        this.clearPad();
      }
    },
    clearPad() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.$refs.signaturePad.style.backgroundImage = `url(${signaturePlaceholder})`;
      this.setCanvasURL("");
    },
    setCanvasURL(value) {
      set(this.item, "signatureEdited", true);
      set(this.item, "value", value);
    },
  },
};
</script>

<style scoped>
.signature-container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
}

.signature-pad__container {
  position: relative;
  background-color: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  box-shadow: 0px 1px 2px 0px #1018280d;
}
.signature__pad {
  border-radius: 2px;
  width: 300px;
  height: 180px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 200px;
  touch-action: none;
}

.signature__pad--disabled {
  pointer-events: none;
}

.signature__clear-btn {
  position: absolute;
  right: 0;
  top: 0;
  color: rgba(0, 0, 0, 0.5);
  border-radius: 2px;
  padding: 3px 10px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: rgba(0, 0, 0, 0.05);
}

.signature__pad--output {
  opacity: 0.00000001;
  position: absolute;
  bottom: 15px;
  z-index: -1;
}

@media only screen and (max-width: 600px) {
  .signature-pad__container {
    width: 100%;
  }
  .signature__pad {
    width: 100%;
  }
}
</style>
